import { API_BASE_URL } from './config';
import type {
  LoginResponse,
  SignupConfig,
  SignupResponse,
  EulaStatusResponse,
  MfaStatusResponse,
  MfaSetupResponse,
  MfaVerifySetupResponse,
} from './types/auth';

export type {
  LoginResponse,
  SignupConfig,
  SignupResponse,
  EulaStatusResponse,
  MfaStatusResponse,
  MfaSetupResponse,
  MfaVerifySetupResponse,
} from './types/auth';

/**
 * Safely parse a JSON response body.
 * Returns the parsed object, or an empty object when the body is empty
 * or not valid JSON (e.g. HTML error page from a proxy).
 */
export async function safeJson(res: Response): Promise<Record<string, unknown>> {
  try {
    const text = await res.text();
    if (!text) return {};
    return JSON.parse(text);
  } catch {
    return {};
  }
}

/** Extract a human-readable error from an API response payload. */
export function extractError(data: Record<string, unknown>, fallback: string): string {
  let msg = fallback;
  if (typeof data.message === 'string' && data.message) msg = data.message;
  else if (typeof data.error === 'string' && data.error) msg = data.error;
  return sanitizeCognitoError(msg);
}

/**
 * Strip AWS Cognito SDK metadata from error messages so users never see
 * internal details like Service name, Status Code, Request ID, or SDK
 * Attempt Count.
 *
 * Example raw message:
 *   "Password did not conform with policy: Password must have uppercase
 *    characters (Service: CognitoIdentityProvider, Status Code: 400,
 *    Request ID: ...) (SDK Attempt Count: 1)"
 *
 * Returns: "Password must have uppercase characters."
 */
export function sanitizeCognitoError(message: string): string {
  if (!message) return message;

  // Strip "(Service: CognitoIdentityProvider, ...)" and everything after
  const serviceBlock = /\(Service: ?CognitoIdentityProvider/.exec(message);
  let cleaned = (serviceBlock ? message.slice(0, serviceBlock.index) : message).trim();

  // Strip standalone "(SDK Attempt Count: N)" if present without Service block
  cleaned = cleaned.replace(/ ?\(SDK Attempt Count: ?\d+\)/, '').trim();

  // Remove the verbose "Password did not conform with policy:" prefix
  const policyPrefix = 'Password did not conform with policy:';
  const idx = cleaned.indexOf(policyPrefix);
  if (idx >= 0) {
    cleaned = cleaned.substring(idx + policyPrefix.length).trim();
  }

  return cleaned || message;
}

export async function getSignupConfig(): Promise<SignupConfig> {
  const response = await fetch(`${API_BASE_URL}/auth/signup-config`);
  if (!response.ok) {
    return { emailDomain: '', restricted: false };
  }
  const data = await safeJson(response);
  return data as unknown as SignupConfig;
}

export async function authSignup(email: string, password: string, displayName?: string, turnstileToken?: string): Promise<SignupResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, displayName: displayName || undefined, turnstileToken: turnstileToken || undefined }),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    // Handle ai-sync users trying to sign up (409 Conflict with AiSyncMigrationRequired)
    if (response.status === 409 && data?.error === 'AiSyncMigrationRequired') {
      throw new AiSyncMigrationRequiredError(
        typeof data.message === 'string' ? data.message : 'This email already exists in Aibuild CAM. Please log in there first.',
        typeof data.camLoginUrl === 'string' ? data.camLoginUrl : '',
      );
    }
    throw new Error(extractError(data, 'Signup failed'));
  }
  return data as unknown as SignupResponse;
}

export async function authResendCode(email: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/resend-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const data = await safeJson(response);
    throw new Error(extractError(data, 'Failed to resend code'));
  }
}

export async function authVerifyEmail(email: string, code: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  if (!response.ok) {
    const data = await safeJson(response);
    throw new Error(extractError(data, 'Verification failed'));
  }
}

/**
 * Thrown when a user exists in ai-sync but has not yet been migrated to Cognito.
 * The user must log in via Aibuild CAM first to complete the migration.
 */
export class AiSyncMigrationRequiredError extends Error {
  public readonly camLoginUrl: string;
  constructor(message: string, camLoginUrl: string) {
    super(message);
    this.name = 'AiSyncMigrationRequiredError';
    this.camLoginUrl = camLoginUrl;
  }
}

export async function authLogin(email: string, password: string): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    // Handle unmigrated ai-sync users (409 Conflict with AiSyncMigrationRequired)
    if (response.status === 409 && data?.error === 'AiSyncMigrationRequired') {
      throw new AiSyncMigrationRequiredError(
        typeof data.message === 'string' ? data.message : 'Please log in via Aibuild CAM first.',
        typeof data.camLoginUrl === 'string' ? data.camLoginUrl : '',
      );
    }
    throw new Error(extractError(data, 'Login failed'));
  }
  return data as unknown as LoginResponse;
}

export async function authMfaVerify(
  mfaToken: string,
  code: string,
  email: string,
  isCognitoMfa: boolean
): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/mfa-verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mfaToken, code, email, isCognitoMfa }),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    throw new Error(extractError(data, 'MFA verification failed'));
  }
  return data as unknown as LoginResponse;
}

export async function authRefreshToken(
  refreshToken: string,
  username: string,
  email?: string,
): Promise<{ accessToken: string; expiresIn: number }> {
  const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken, username, email }),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    throw new Error(extractError(data, 'Token refresh failed'));
  }
  return data as unknown as { accessToken: string; expiresIn: number };
}

export async function authForgotPassword(email: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const data = await safeJson(response);
    // Handle unmigrated ai-sync users (409 Conflict with AiSyncMigrationRequired)
    if (response.status === 409 && data?.error === 'AiSyncMigrationRequired') {
      throw new AiSyncMigrationRequiredError(
        typeof data.message === 'string' ? data.message : 'Please log in via Aibuild CAM first.',
        typeof data.camLoginUrl === 'string' ? data.camLoginUrl : '',
      );
    }
    throw new Error(extractError(data, 'Request failed'));
  }
}

export async function authSsoInit(accessToken: string): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/sso-init`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ accessToken }),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    throw new Error(extractError(data, 'SSO initialization failed'));
  }
  return data as unknown as LoginResponse;
}

export async function authConfirmPassword(email: string, code: string, newPassword: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/confirm-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code, newPassword }),
  });
  if (!response.ok) {
    const data = await safeJson(response);
    throw new Error(extractError(data, 'Password reset failed'));
  }
}

export async function authGetEulaStatus(accessToken: string): Promise<EulaStatusResponse> {
  const response = await fetch(`${API_BASE_URL}/api/v1/eula/status`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const data = await safeJson(response);
  if (!response.ok) {
    throw new Error(extractError(data, 'Failed to check EULA status'));
  }
  return data as unknown as EulaStatusResponse;
}

export async function authAcceptEula(accessToken: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/v1/eula/accept`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (!response.ok) {
    const data = await safeJson(response);
    throw new Error(extractError(data, 'Failed to accept EULA'));
  }
}

// ========================================================================
// MFA Management
// ========================================================================

export async function getMfaStatus(accessToken: string): Promise<MfaStatusResponse> {
  const response = await fetch(`${API_BASE_URL}/api/v1/mfa/status`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const data = await safeJson(response);
  if (!response.ok) {
    throw new Error(extractError(data, 'Failed to get MFA status'));
  }
  return data as unknown as MfaStatusResponse;
}

export async function startMfaSetup(accessToken: string): Promise<MfaSetupResponse> {
  const response = await fetch(`${API_BASE_URL}/api/v1/mfa/setup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
  const data = await safeJson(response);
  if (!response.ok) {
    throw new Error(extractError(data, 'Failed to start MFA setup'));
  }
  return data as unknown as MfaSetupResponse;
}

export async function verifyMfaSetup(accessToken: string, code: string): Promise<MfaVerifySetupResponse> {
  const response = await fetch(`${API_BASE_URL}/api/v1/mfa/verify-setup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ code }),
  });
  const data = await safeJson(response);
  if (!response.ok) {
    throw new Error(extractError(data, 'Failed to verify MFA code'));
  }
  return data as unknown as MfaVerifySetupResponse;
}

export async function disableMfa(accessToken: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/v1/mfa/disable`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (!response.ok) {
    const data = await safeJson(response);
    throw new Error(extractError(data, 'Failed to disable MFA'));
  }
}
